import React, { useEffect, useState, useMemo } from 'react';
import styles from './FourColumnGrid.module.css'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import Facebook from '../assets/socials/facebook.jsx';
import XIcon from '../assets/socials/x.jsx';
import Instagram from '../assets/socials/instagram.jsx';
import LinkedIn from '../assets/socials/LinkedIn.jsx';
import Vimeo from '../assets/socials/Vimeo.jsx';
import YouTube from '../assets/socials/YouTube.jsx';
import Arrow from '../assets/arrowTopRight.jsx';
import { useParams } from "react-router-dom";

function mergeRichText(a, b) {
    if (!a && !b) return null;
    if (!a) return b;
    if (!b) return a;
    if (a.nodeType === 'document' && b.nodeType === 'document') {
        return {
            ...a,
            content: [...(a.content || []), ...(b.content || [])],
        };
    }
    // fallback: wrap strings or unknown nodes into a document-like object
    const aContent = a && a.nodeType === 'document' ? a.content : [{ nodeType: 'paragraph', content: [{ nodeType: 'text', value: String(a) }] }];
    const bContent = b && b.nodeType === 'document' ? b.content : [{ nodeType: 'paragraph', content: [{ nodeType: 'text', value: String(b) }] }];
    return { nodeType: 'document', data: {}, content: [...aContent, ...bContent] };
}

function FourColumnGrid({ title, items = [] }) {

    const { lang } = useParams();

    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)').matches : false
    );

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const mq = window.matchMedia('(max-width: 980px)');
        const onChange = (e) => setIsMobile(e.matches);
        if (mq.addEventListener) mq.addEventListener('change', onChange);
        else mq.addListener(onChange);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', onChange);
            else mq.removeListener(onChange);
        };
    }, []);

    // prepare items adjusted for mobile: append 3rd -> 1st and 4th -> 2nd, then clear those firstRows / move socials in 3rd/4th
    const adjustedItems = useMemo(() => {
        if (!isMobile || items.length < 4) {
            // ensure hasSocials flag exists for desktop render (only 4th column shows socials)
            return items.map((itm, idx) => ({ ...itm, fields: { ...itm.fields, hasSocials: idx === 3 } }));
        }

        // clone and add fields we will mutate (avoid mutating original items)
        const cloned = items.map((itm, idx) => {
            const newItm = { ...itm, fields: { ...itm.fields } };
            newItm.fields.firstRow = itm.fields.firstRow ?? null;
            newItm.fields.secondRow = itm.fields.secondRow ?? null;
            newItm.fields.thirdRow = itm.fields.thirdRow ?? null;
            newItm.fields.hasSocials = idx === 3; // mark original socials owner
            return newItm;
        });

        // merge rows according to mobile layout rules:
        // - append 3rd.firstRow -> 1st.firstRow
        // - append 4th.firstRow -> 2nd.firstRow (and move socials flag)
        // - append 3rd.secondRow -> 1st.thirdRow
        // - append 3rd.thirdRow  -> 2nd.thirdRow
        // and then clear moved content from the 3rd/4th items
        return cloned.map((itm, idx, arr) => {
            if (idx === 0) {
                // first column: get its own firstRow + third.firstRow
                itm.fields.firstRow = mergeRichText(arr[0]?.fields?.firstRow, arr[2]?.fields?.firstRow);
                // append third item's secondRow into first column's thirdRow
                itm.fields.thirdRow = mergeRichText(arr[0]?.fields?.thirdRow, arr[2]?.fields?.secondRow);
            }
            if (idx === 1) {
                // second column: append fourth.firstRow into second.firstRow
                itm.fields.firstRow = mergeRichText(arr[1]?.fields?.firstRow, arr[3]?.fields?.firstRow);
                // move socials flag from fourth into second
                if (arr[3]?.fields?.hasSocials) {
                    itm.fields.hasSocials = true;
                }
                // append third item's thirdRow into second column's thirdRow
                itm.fields.thirdRow = mergeRichText(arr[1]?.fields?.thirdRow, arr[2]?.fields?.thirdRow);
            }
            if (idx === 2 || idx === 3) {
                // clear moved content from 3rd and 4th columns so it doesn't duplicate
                itm.fields.firstRow = null;
                itm.fields.secondRow = null;
                itm.fields.thirdRow = null;
                itm.fields.hasSocials = false;
            }
            return itm;
        });
    }, [isMobile, items]);

    // Custom render options to inject <Arrow /> into <a> tags
    const renderOptions = {
        renderNode: {
            'hyperlink': (node, children) => {
                const url = node.data.uri;
                return (
                    <a href={url} target="_blank" rel="noopener noreferrer">
                        <span className={styles.label}>{children}</span>
                        <span className={styles.arrow}><Arrow /></span>
                    </a>
                );
            }
        }
    };

    return (
        <div className={`${styles.four_column_grid} ${lang === "ko" && styles.lang_ko}`}>
            <div className={styles.section_title}>
                <h2>{title}</h2>
            </div>
            <div className={styles.grid}>
                {adjustedItems.map((item, i) => (
                    <div key={i} className={styles.column}>
                        <div className={`${styles.firstRow} ${styles.row}`}>
                            <div className={`${styles.text_wrap} preserve-line-breaks`}>
                                {item.fields.firstRow && documentToReactComponents(item.fields.firstRow)}
                                {item.fields.hasSocials && (
                                    <div className={styles.socials}>
                                        <a href="https://www.facebook.com/eyelinestudios/" target="_blank" rel="noopener noreferrer">
                                            <Facebook />
                                        </a>
                                        <a href="https://x.com/eyelinestudios" target="_blank" rel="noopener noreferrer">
                                            <XIcon />
                                        </a>
                                        <a href="https://www.instagram.com/eyelinestudios/" target="_blank" rel="noopener noreferrer">
                                            <Instagram />
                                        </a>
                                        <a href="https://www.linkedin.com/company/eyelinestudios/" target="_blank" rel="noopener noreferrer">
                                            <LinkedIn />
                                        </a>
                                        <a href="https://vimeo.com/eyelinestudios" target="_blank" rel="noopener noreferrer">
                                            <Vimeo />
                                        </a>
                                        <a href="https://www.youtube.com/@eyelinestudios" target="_blank" rel="noopener noreferrer">
                                            <YouTube />
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className={`${styles.secondRow} ${styles.row}`}>
                            <div className={`${styles.text_wrap} `}>
                                {item.fields.secondRow && documentToReactComponents(item.fields.secondRow, renderOptions)}
                            </div>
                        </div>
                        <div className={`${styles.thirdRow} ${styles.row}`}>
                            <div className={`${styles.text_wrap}`}>
                                {item.fields.thirdRow && documentToReactComponents(item.fields.thirdRow, renderOptions)}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default FourColumnGrid;